import { videoPlayback, type BodyPart, type VideoPart } from "@/lib/activityAnatomy";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const MAX_DURATION_SEC = 12 * 60 * 60;
export const SEEK_SLACK_SEC = 0.8;

export type WatchEntry = {
  maxSeconds: number;
  durationSeconds: number;
  ended: boolean;
};

export type WatchMap = Record<string, WatchEntry>;

function videoKind(url: string) {
  return videoPlayback(url)?.kind || null;
}

export function isLockableVideoUrl(url: string) {
  return videoKind(url) === "file";
}

export function isEmbedVideoUrl(url: string) {
  const kind = videoKind(url);
  return kind === "youtube" || kind === "vimeo";
}

function videoParts(body: unknown): VideoPart[] {
  if (!Array.isArray(body)) return [];
  return (body as BodyPart[]).filter(
    (part): part is VideoPart => Boolean(part && part.type === "video" && String(part.url || "").trim()),
  );
}

export function lockableVideoParts(body: unknown) {
  return videoParts(body).filter((part) => isLockableVideoUrl(part.url));
}

export function embedVideoParts(body: unknown) {
  return videoParts(body).filter((part) => isEmbedVideoUrl(part.url));
}

export function videoWatchKey(part: { mediaId?: string } | null | undefined, index: number) {
  const id = String(part?.mediaId || "").trim();
  if (UUID_RE.test(id)) return id;
  return `v${Number(index) || 0}`;
}

function clampWatchSeconds(value: unknown, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.min(MAX_DURATION_SEC, n);
}

export function normalizeWatchEntry(raw: unknown): WatchEntry {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { maxSeconds: 0, durationSeconds: 0, ended: false };
  }
  const src = raw as Record<string, unknown>;
  const durationSeconds = clampWatchSeconds(src.durationSeconds);
  const maxSeconds = Math.min(
    durationSeconds || clampWatchSeconds(src.maxSeconds),
    clampWatchSeconds(src.maxSeconds),
  );
  return {
    maxSeconds,
    durationSeconds,
    ended: src.ended === true,
  };
}

export function normalizeWatchMap(raw: unknown): WatchMap {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: WatchMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const id = String(key || "").trim();
    if (!id || id.length > 80) continue;
    out[id] = normalizeWatchEntry(value);
  }
  return out;
}

function watchSlack(durationSeconds: number) {
  const duration = Number(durationSeconds) || 0;
  if (duration <= 0) return 2;
  return Math.max(1, Math.min(2, duration * 0.02));
}

export function watchEntrySatisfied(entry: unknown) {
  const row = normalizeWatchEntry(entry);
  if (row.durationSeconds <= 0) return false;
  return row.maxSeconds + watchSlack(row.durationSeconds) >= row.durationSeconds;
}

export function watchMapSatisfied(body: unknown, watchMap: unknown) {
  const files = lockableVideoParts(body);
  if (!files.length) return true;
  const map = normalizeWatchMap(watchMap);
  return files.every((part, index) => watchEntrySatisfied(map[videoWatchKey(part, index)]));
}

export function mergeWatchEntry(prev: unknown, incoming: unknown): WatchEntry {
  const a = normalizeWatchEntry(prev);
  const b = normalizeWatchEntry(incoming);
  const durationSeconds = Math.max(a.durationSeconds, b.durationSeconds);
  const farthest = Math.max(a.maxSeconds, b.maxSeconds);
  return {
    maxSeconds: durationSeconds ? Math.min(durationSeconds, farthest) : farthest,
    durationSeconds,
    ended: a.ended || b.ended,
  };
}
